import { DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
  AgentProfileDto,
  AgentProfileId,
  AgentProfilePublicDto,
  AgentServiceOffering,
  AgentSpecialty,
  AgentUserPublicDto,
  FRENCH_DEPARTMENTS_MAP,
  FRENCH_REGIONS,
  FrenchDepartment,
  OpeningHoursDto,
} from '@workspace/data-types';
import { AuraOpeningHoursDisplayComponent, CustomTitleStrategy } from '@workspace/ng-aura';
import { environment } from '@workspace/ng-config';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { AgentCardDisplayComponent } from '../../../../agent-card';

@Component({
  selector: 'etxe-agent-profile-public',
  templateUrl: './agent-profile-public.component.html',
  imports: [
    RouterLink,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatTooltipModule,
    DatePipe,
    DecimalPipe,
    UpperCasePipe,
    AgentCardDisplayComponent,
    AuraOpeningHoursDisplayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentProfilePublicComponent {
  agentProfileId = input<AgentProfileId | null | undefined>(null);
  profileData = input<AgentProfilePublicDto | AgentProfileDto | null>(null);
  showPublicCtas = input<boolean>(true);
  avatarUrl = input<string | null | undefined>(undefined);

  readonly agentProfile = signal<AgentProfilePublicDto | AgentProfileDto | null>(null);
  readonly isLoading = signal<boolean>(true);
  readonly hasError = signal<boolean>(false);

  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly titleStrategy = inject(CustomTitleStrategy);

  readonly ownerAppUrl = environment.apps.ownerUrl;

  // Extracted computed signals
  readonly user = computed(() => {
    const u = this.agentProfile()?.user;
    return typeof u === 'object' && u !== null ? (u as AgentUserPublicDto) : null;
  });
  readonly firstName = computed(() => this.user()?.identity?.firstName || '');
  readonly lastName = computed(() => this.user()?.identity?.lastName || '');
  readonly fullName = computed(() => {
    const f = this.firstName();
    const l = this.lastName();
    if (!f && !l) return 'Agent immobilier certifié';
    return `${f} ${l}`.trim();
  });
  readonly effectiveAvatarUrl = computed(() => {
    const custom = this.avatarUrl();
    if (custom !== undefined) {
      return custom;
    }
    return this.user()?.identity?.avatar?.url || null;
  });
  readonly initials = computed(() => {
    const f = this.firstName().charAt(0);
    const l = this.lastName().charAt(0);
    return (f + l).toUpperCase() || 'AG';
  });

  readonly cards = computed(() => this.agentProfile()?.cards);
  readonly activeCard = computed(() => this.cards()?.activeCard ?? null);
  readonly isCardActive = computed(() => this.activeCard()?.isActive !== false);

  readonly activity = computed(() => this.agentProfile()?.activity);
  readonly network = computed(() => this.activity()?.network || null);
  readonly specialties = computed(() => this.activity()?.specialties || []);
  readonly bio = computed(() => this.activity()?.bio || '');
  readonly services = computed(() => this.activity()?.services || []);
  readonly socialLinks = computed(() => this.activity()?.socialLinks || null);
  readonly feeScaleUrl = computed(() => this.activity()?.feeScaleUrl || null);
  readonly averageCommissionRate = computed(() => this.activity()?.averageCommissionRate ?? null);

  readonly intervention = computed(() => this.agentProfile()?.intervention);
  readonly primaryRegion = computed(() => this.intervention()?.primaryRegion || null);
  readonly interventionZones = computed(() => this.intervention()?.zones || []);
  readonly preferredCities = computed(() => this.intervention()?.preferredCities || []);

  readonly work = computed(() => this.agentProfile()?.work);
  readonly officeAddress = computed(() => this.work()?.officeAddress || null);
  readonly openingHours = computed<OpeningHoursDto | null>(() => {
    return (this.work()?.openingHours ?? null) as OpeningHoursDto | null;
  });

  readonly hasOpeningHours = computed<boolean>(() => {
    const hours = this.openingHours();
    if (!hours) return false;
    const days: Array<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    return days.some((day) => Boolean(hours[day]?.isOpen));
  });

  readonly openingHoursStatus = computed(() => {
    const hours = this.openingHours();
    if (!hours) return null;

    const currentJsDay = new Date().getDay();
    const dayKeys: Array<{
      key: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      jsDay: number;
    }> = [
      { key: 'monday', jsDay: 1 },
      { key: 'tuesday', jsDay: 2 },
      { key: 'wednesday', jsDay: 3 },
      { key: 'thursday', jsDay: 4 },
      { key: 'friday', jsDay: 5 },
      { key: 'saturday', jsDay: 6 },
      { key: 'sunday', jsDay: 0 },
    ];

    const todayConfig = dayKeys.find((d) => d.jsDay === currentJsDay);
    const today = todayConfig ? hours[todayConfig.key] : undefined;

    if (!today || !today.isOpen) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }

    if (today.byAppointmentOnly) {
      return {
        isOpen: true,
        text: 'Sur rendez-vous aujourd’hui',
        badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
      };
    }

    const slots: string[] = [];
    if (today.morningOpen && today.morningClose) {
      slots.push(`${today.morningOpen} - ${today.morningClose}`);
    }
    if (today.afternoonOpen && today.afternoonClose) {
      slots.push(`${today.afternoonOpen} - ${today.afternoonClose}`);
    }

    if (slots.length === 0) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }

    return {
      isOpen: true,
      text: `Ouvert aujourd’hui (${slots.join(' & ')})`,
      badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
    };
  });

  readonly contactInfos = computed(() => {
    const profile = this.agentProfile();
    return profile && 'settings' in profile
      ? (profile as AgentProfileDto).settings?.contactInfos || null
      : null;
  });
  readonly isVacationMode = computed(() => this.agentProfile()?.isVacationMode ?? false);

  readonly specialtyMeta: Record<string, { label: string; icon: string }> = {
    [AgentSpecialty.APARTMENT]: { label: 'Appartements', icon: 'apartment' },
    [AgentSpecialty.HOUSE]: { label: 'Maisons & Villas', icon: 'home' },
    [AgentSpecialty.PRESTIGE]: { label: 'Prestige & Luxe', icon: 'diamond' },
    [AgentSpecialty.BUILDING]: { label: 'Immeubles de rapport', icon: 'domain' },
    [AgentSpecialty.LAND]: { label: 'Terrains constructibles', icon: 'landscape' },
    [AgentSpecialty.COMMERCIAL]: { label: 'Commerces & Locaux', icon: 'storefront' },
  };

  readonly serviceMeta: Record<string, { label: string; icon: string }> = {
    [AgentServiceOffering.PHOTO_PRO]: { label: 'Photographie professionnelle', icon: 'photo_camera' },
    [AgentServiceOffering.VIRTUAL_TOUR]: { label: 'Visite immersive 3D', icon: 'view_in_ar' },
    [AgentServiceOffering.DRONE]: { label: 'Prises de vue par drone', icon: 'flight' },
    [AgentServiceOffering.HOME_STAGING]: { label: 'Home staging & Valorisation', icon: 'auto_awesome' },
    [AgentServiceOffering.FINANCIAL_SCREENING]: {
      label: 'Filtrage financier acquéreurs',
      icon: 'verified_user',
    },
    [AgentServiceOffering.REPORT_24H]: { label: 'Compte-rendu de visite sous 24h', icon: 'schedule' },
    [AgentServiceOffering.PREMIUM_PORTALS]: { label: 'Diffusion portails premium', icon: 'campaign' },
    [AgentServiceOffering.NOTARY_SUPPORT]: { label: 'Accompagnement notarial', icon: 'gavel' },
  };

  constructor() {
    effect(() => {
      const name = this.fullName();
      if (name && name !== 'Agent immobilier certifié') {
        this.titleStrategy.setDynamicTitle(`${name} - Agent immobilier certifié`);
      }
    });

    effect(() => {
      const explicitData = this.profileData();
      if (explicitData) {
        this.agentProfile.set(explicitData);
        this.isLoading.set(false);
        this.hasError.set(false);
        return;
      }

      const id = this.agentProfileId();
      if (!id) {
        this.isLoading.set(false);
        this.hasError.set(true);
        return;
      }

      this.isLoading.set(true);
      this.hasError.set(false);

      this.api
        .business()
        .etxe()
        .public()
        .agents()
        .get(id)
        .pipe(
          tap((profile) => {
            this.agentProfile.set(profile);
            this.hasError.set(false);
          }),
          catchError((err) => {
            console.error('Failed to load public agent profile', err);
            this.hasError.set(true);
            return EMPTY;
          }),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }

  getSpecialtyInfo(key: string) {
    return this.specialtyMeta[key] || { label: key, icon: 'category' };
  }

  getServiceInfo(key: string) {
    return this.serviceMeta[key] || { label: key, icon: 'check_circle' };
  }

  getRegionName(regCode?: string | null): string {
    if (!regCode) return '';
    return FRENCH_REGIONS.find((r) => r.code === regCode)?.name || regCode;
  }

  getDepartmentLabel(deptCode: string): string {
    const info = FRENCH_DEPARTMENTS_MAP[deptCode as FrenchDepartment];
    return info ? `${info.name} (${info.code})` : deptCode;
  }
}
